import type { FC } from "react";
import { useCallback } from "react";

import { useDispatch } from "react-redux";

import { SWReactIcons } from "@similarweb/icons";
import { SearchInput } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import ListPreLoaderItem from "app/pages/sales-intelligence/common-components/ListPreLoaderItem/ListPreLoaderItem";
import { SavedSearchItem } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/components/SavedSearchitem/SavedSearchItem";
import { Sidebar } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/components/Sidebar/Sidebar";
import type { SavedSearchesDto } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/types/dto";
import { updateSavedSearchNameThunk } from "app/pages/sales-intelligence/sub-modules/advanced-search/store/effects";
import type { ThunkDispatchCommon } from "single-spa/store/types";

import SavedSearchDeleteModalContainer from "../../containers/SavedSearchDeleteModal";

import {
    Overlay,
    SavedSearchesBodyContainer,
    SavedSearchesListContainer,
    SavedSearchesListTitle,
    SearchContainer,
    SIDEBAR_CLOSE_BTN_CLASS,
    SIDEBAR_COMPONENT_CLASS,
    StyledNoData,
    StyledPanelTitle,
    StyledSidebarContainer,
} from "./styles";

interface ISavedSearchPanelProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    saveSearches: readonly SavedSearchesDto[];
    onRefetch: () => void;
    setSearchText: (searchText: string) => void;
    searchText: string;
    isLoading: boolean;
    totalCount: number;
}

export const SavedSearchPanel: FC<ISavedSearchPanelProps> = ({
    isOpen,
    onClose,
    title,
    saveSearches,
    onRefetch,
    searchText,
    setSearchText,
    isLoading,
    totalCount,
}) => {
    const dispatch: ThunkDispatchCommon = useDispatch();

    const handleOnSidebarToggle = useCallback(
        (status: boolean) => {
            if (!status) {
                onClose();
            }
        },
        [onClose],
    );

    const handleOnSearch = (text: string) => {
        setSearchText(text);
    };

    const handleRename = async (item: SavedSearchesDto, newName: string) => {
        if (newName !== item.name) {
            const resultAction = await dispatch(updateSavedSearchNameThunk(item.id, newName));

            if (resultAction) {
                await onRefetch();
            }
        }
    };

    return (
        <StyledSidebarContainer>
            <SavedSearchDeleteModalContainer onDelete={onRefetch} />
            <Overlay
                isOpen={isOpen}
                onClick={onClose}
            />
            <Sidebar
                isSidebarOpen={isOpen}
                onSidebarToggle={handleOnSidebarToggle}
                sidebarClassName={SIDEBAR_COMPONENT_CLASS}
                closeButtonClassName={SIDEBAR_CLOSE_BTN_CLASS}
                onCloseButtonClick={onClose}
            >
                <StyledPanelTitle>
                    <SWReactIcons
                        iconName={"orange-search-v2-outline"}
                        size={"md"}
                    />
                    {title}
                </StyledPanelTitle>
                <SavedSearchesBodyContainer>
                    <SearchContainer>
                        <SearchInput
                            defaultValue={searchText}
                            debounce={500}
                            onChange={handleOnSearch}
                            placeholder={translate("si.advanced_search.my-saved-searches.search.placeholder")}
                        />
                    </SearchContainer>
                    <I18n
                        element={SavedSearchesListTitle}
                        translatable={{ key: "si.advanced_search.my-saved-searches.list.title" }}
                    />
                    {isLoading ? <ListPreLoaderItem rowNumber={3} /> : null}
                    {!isLoading && totalCount === 0 && searchText.length > 0 ? (
                        <StyledNoData
                            subtitle=""
                            titleSize={16}
                        />
                    ) : null}
                    <SavedSearchesListContainer>
                        {saveSearches.map((item: SavedSearchesDto) => (
                            <SavedSearchItem
                                item={item}
                                onRename={handleRename}
                            />
                        ))}
                    </SavedSearchesListContainer>
                </SavedSearchesBodyContainer>
            </Sidebar>
        </StyledSidebarContainer>
    );
};
